import React, { useCallback, useEffect, useState, Fragment } from 'react'
import classnames from 'classnames/bind'
import { filters } from '@adc/blueprint'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
// @ts-ignore need to type this
import { formFieldWrapper } from '../formFieldWrapper'
import Button from '../../elementals/button'
import { ColorNames } from '../../../constants/colorVars'
import { FormFieldCommonProps } from '../../../types'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const BREAKPOINT_TO_TRUNCATE = 500
const MOBILE_STRING_SLICE_SIZE = 3

export interface InlineSelectFieldOption {
  value: string | number
  text: string
  disabled?: boolean
}

export interface InlineSelectFieldProps
  extends Omit<FormFieldCommonProps, 'onChange'> {
  compact?: boolean
  options: InlineSelectFieldOption[]
  value?: string | number | (string | number)[]
  onChange?: (value: string | number | (string | number)[]) => void
  onBlur?: () => void
  onFocus?: () => void
  className?: string
  showAnyOption?: boolean
  anyOptionText?: string
  mode?: 'single' | 'multi' | 'range'
  disabled?: boolean
  focused?: boolean
}

/**
 * InlineSelectField component
 *
 * A horizontal row of button-like options that supports single-select, multi-select, and range selection modes.
 * Includes an optional "Any" option that represents no filtering/selection.
 */
const InlineSelectField: React.FC<InlineSelectFieldProps> = ({
  elmId,
  options: _options,
  value,
  mode = 'single',
  onChange,
  onBlur,
  onFocus,
  showError = false,
  fluid = false,
  hideWrapper = false,
  focused = false,
  disabled = false,
  compact = false,
  className = '',
  showAnyOption = true,
  anyOptionText = 'Any',
}) => {
  const { width } = useWindowDimensions()
  const [options, setOptions] = useState(_options)

  const {
    getModeDescription,
    getSelectionSummary,
    handleAnyClick,
    handleOptionClick,
    isAnySelected,
    isOptionSelected,
    normalizedValue,
  } = filters.hooks.useInlineSelectFilter({
    mode,
    onChange,
    options,
    value,
  })

  useEffect(() => {
    if (width < BREAKPOINT_TO_TRUNCATE) {
      const truncatedOptions = _options.map(option => {
        const numberMatch = option.text.match(/^(\d+(?:\.\d+)?)(.*)$/)

        if (numberMatch) {
          const numericPart = numberMatch[1]
          const suffix = numberMatch[2]
          const numericValue = parseFloat(numericPart)

          const rounded = Math.round(numericValue * 10) / 10

          let formattedNumber = rounded.toString()
          if (formattedNumber.includes('.') && formattedNumber.endsWith('0')) {
            formattedNumber = formattedNumber.slice(0, -2)
          }

          return {
            ...option,
            text: `${formattedNumber}${suffix}`,
          }
        } else {
          const shouldTruncate = option.text.length > MOBILE_STRING_SLICE_SIZE

          return {
            ...option,
            text: shouldTruncate
              ? `${option.text.slice(0, MOBILE_STRING_SLICE_SIZE)}.`
              : option.text,
          }
        }
      })
      setOptions(truncatedOptions)
    } else {
      setOptions(_options)
    }
  }, [width, _options])

  const handleFocus = useCallback(() => {
    if (onFocus) onFocus()
  }, [onFocus])

  const handleBlur = useCallback(() => {
    if (onBlur) onBlur()
  }, [onBlur])

  const wrapperClasses = cx(
    'inline-select-field',
    'field',
    {
      fluid,
      error: showError,
      focused,
      disabled,
      compact,
    },
    className
  )

  const renderOptions = () => (
    <>
      {showAnyOption && (
        <Button
          key='any'
          role={isAnySelected ? 'secondary' : 'tertiary'}
          size={compact ? 'small' : 'medium'}
          color={isAnySelected ? ColorNames.Action : ColorNames.Gray}
          isSelected={isAnySelected}
          onClick={handleAnyClick}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          elmId={`${elmId}_any_option`}
          className={cx('option-button', { compact, selected: isAnySelected })}
          aria-pressed={isAnySelected}
          aria-describedby={`${elmId}_description`}
          aria-label={`${anyOptionText}, clear all selections`}
        >
          {anyOptionText}
        </Button>
      )}
      <div key='gap-filler-any' className={cx('gap-filler')} />
      {options.map((option, index) => {
        const isSelected = isOptionSelected(option.value)
        const isOptionDisabled = disabled || option.disabled
        const isPrevSelected =
          index > 0 && isOptionSelected(options[index - 1].value)
        const isNextSelected =
          index < options.length - 1 &&
          isOptionSelected(options[index + 1].value)

        // Create descriptive aria-label based on state and mode
        const getAriaLabel = () => {
          let label = option.text
          if (isSelected) {
            label += ', selected'
          }
          if (isOptionDisabled) {
            label += ', disabled'
          }
          if (mode === 'multi') {
            label += isSelected ? ', click to deselect' : ', click to select'
          } else if (mode === 'range') {
            if (normalizedValue.length === 0) {
              label += ', click to start range selection'
            } else if (normalizedValue.length === 1) {
              label += ', click to complete range selection'
            } else {
              label += ', click to start new range'
            }
          }
          return label
        }

        return (
          <Fragment key={option.value}>
            <Button
              key={option.value}
              role={isSelected ? 'secondary' : 'tertiary'}
              size={compact ? 'small' : 'medium'}
              color={isSelected ? ColorNames.Action : ColorNames.Gray}
              isSelected={isSelected}
              onClick={() => handleOptionClick(option.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={isOptionDisabled}
              elmId={`${elmId}_option_${option.value}`}
              className={cx(
                'option-button',
                {
                  selected: isSelected,
                  'prev-selected': isPrevSelected,
                  'next-selected': isNextSelected,
                  compact,
                },
                'super-compact'
              )}
              aria-pressed={isSelected}
              aria-describedby={`${elmId}_description`}
              aria-label={getAriaLabel()}
              aria-setsize={options.length + (showAnyOption ? 1 : 0)}
              aria-posinset={index + (showAnyOption ? 2 : 1)}
            >
              {option.text}
            </Button>
            {index < options.length - 1 && (
              <div
                key={`gap-${index}`}
                className={cx('gap-filler', {
                  selected: isSelected && isNextSelected,
                })}
              />
            )}
          </Fragment>
        )
      })}
    </>
  )

  if (hideWrapper) {
    return (
      <>
        {/* Hidden description for screen readers */}
        <div id={`${elmId}_description`} className='sr-only'>
          {getModeDescription} {getSelectionSummary}
        </div>
        {renderOptions()}
      </>
    )
  }

  return (
    <div
      className={wrapperClasses}
      data-elm-id={elmId}
      role='group'
      aria-label={`${mode} selection field`}
      aria-describedby={`${elmId}_description ${elmId}_summary`}
      aria-required={false}
      aria-invalid={showError}
      aria-disabled={disabled}
    >
      {/* Hidden description for screen readers */}
      <div id={`${elmId}_description`} className='sr-only'>
        {getModeDescription}
      </div>
      {/* Live region for selection updates */}
      <div
        id={`${elmId}_summary`}
        className='sr-only'
        aria-live='polite'
        aria-atomic='true'
      >
        {getSelectionSummary}
      </div>
      {renderOptions()}
    </div>
  )
}

// eslint-disable-next-line
const InlineSelectFieldWrapped = formFieldWrapper(
  (fieldProps: unknown, showError: boolean | undefined) => {
    // @ts-ignore next
    return <InlineSelectField {...fieldProps} showError={showError} />
  }
)

export default InlineSelectFieldWrapped
